import { ACTIVITY_STATE } from "Constants/constants";
import { selectAgentReportByActivityId } from "Reducers/activity/agentReport.selectors";
import { selectProjectActivityById } from "Reducers/activity/activity.selectors";
import { loadProjectActivity } from "Reducers/activity/thunks/loadProjectActivity.thunk";
import { loadLogFromActivity, streamFinished } from "Reducers/log";
import {
  runEndedWithoutReport,
  runReported,
  runStarted,
  selectTroubleshootingRun
} from "Reducers/troubleshootingRuns";
import { startAppListening } from "Store/listenerMiddleware";

import { notifyTroubleshootingCompleted } from "./troubleshootingRun.notifications";

import type { RootState } from "Store/configureStore";
import type { AppStartListening } from "Store/listenerMiddleware";

/**
 * How long to wait for the new run's activity object to reach the store. The run
 * thunk awaits a task refetch before resolving, so it is usually already there;
 * this covers the case where the refetch failed (deliberately swallowed) and the
 * project websocket has to deliver it.
 */
const ACTIVITY_TIMEOUT_MS = 15_000;

/** The run is over and has nothing further to write. */
const TERMINAL_ACTIVITY_STATES: string[] = [
  ACTIVITY_STATE.CANCELLED,
  ACTIVITY_STATE.COMPLETE
];

/**
 * How long to wait for a run's activity to say it is over.
 *
 * Twenty minutes is the task container's own ceiling, so a run that has not
 * settled by then is waiting on something that is not coming. Giving up leaves
 * the run tracked for the session — the safe direction, since the alternative
 * invites a second $5-10 run on a failure that may still be under diagnosis.
 */
const RUN_SETTLE_TIMEOUT_MS = 20 * 60_000;

/**
 * How long a settled run's log gets to deliver its report before the run is
 * called reportless. The report is the agent's LAST stdout line, written just
 * before the container exits, so it can still be a poll or two behind the state
 * change — concluding "no report" the instant the activity settles would lose
 * the toast on a run that reported perfectly. A report arriving ends this wait
 * early, so only a genuinely reportless run pays the full 15 seconds.
 */
const REPORT_GRACE_MS = 15_000;

/**
 * Owns everything a troubleshooting run needs AFTER the POST succeeds, so no
 * component has to stay mounted for it:
 *
 *  1. streams the new activity's log, which is what makes the report appear in
 *     the open panel and what the completion toast is derived from;
 *  2. settles the run once its activity is over — untracking it so a run that
 *     never reported stops suppressing its failure's CTA;
 *  3. fires the completion toast the moment that log parses to a report.
 *
 * The stream is the load-bearing part. `loadLogFromActivity` spawns a worker
 * owned by a thunk closure, not by a component, and nothing terminates it — so
 * starting it here means a run reports itself even if the user closes the panel,
 * closes the log modal, and navigates to another page.
 *
 * The ACTIVITY is what decides a run is finished, never the stream. A closed
 * stream is not proof: `stream.worker.js` posts the same `done: true` whether
 * the log sealed or it simply gave up on a quiet one, and `log[id].streamEnded`
 * is shared by every worker on that activity, so it can be set by a viewer's
 * stream while the run's own is still polling. Reading the activity's state
 * instead means no stream closing early can end a run early.
 *
 * Kept in this feature's folder rather than with the `log` slice: only the
 * troubleshooting run needs this policy, and removing the feature removes it.
 */
export const registerTroubleshootingAgentListeners = (
  startListening: AppStartListening
) => {
  // 1. Resolve the run's activity, stream its log, then settle the run.
  startListening({
    actionCreator: runStarted,
    effect: async (action, { dispatch, getState, condition }) => {
      const { activityId, organizationId, projectId } = action.payload;
      const params = { organizationId, projectId, activityId };

      const readActivity = (state: RootState) =>
        selectProjectActivityById(state, params);

      if (!readActivity(getState())) {
        // Not in the project activity store yet — ask for it directly rather
        // than waiting on a poll tick, then wait for whichever source wins.
        dispatch(
          loadProjectActivity({
            organizationDescriptionId: organizationId,
            projectDescriptionId: projectId,
            activityId
          })
        );

        const arrived = await condition(
          (_, currentState) => Boolean(readActivity(currentState)),
          ACTIVITY_TIMEOUT_MS
        );
        if (!arrived) return;
      }

      const activity = readActivity(getState());
      if (!activity) return;

      // `loadLogFromActivity` reads `activity._links.log.href`, which is why the
      // whole object is needed and an id alone will not do. It keeps the worker
      // open for the whole run on its own, off the activity's task type.
      dispatch(loadLogFromActivity({ activity }));

      const isTracked = () =>
        Boolean(selectTroubleshootingRun(getState(), activityId));
      const hasReport = () =>
        Boolean(selectAgentReportByActivityId(getState(), activityId));

      const hasSettled = (state: RootState) => {
        const current = selectProjectActivityById(state, params);
        return Boolean(
          current && TERMINAL_ACTIVITY_STATES.includes(current.state)
        );
      };

      // One wait per run, started here rather than off the stream, so a run
      // leaves exactly one pending effect behind however many times its log is
      // streamed. It also ends the moment the run is settled elsewhere — a run
      // that reports untracks itself in listener 2 — rather than re-evaluating
      // on every action for twenty minutes with nothing left to decide.
      if (!hasSettled(getState())) {
        const settled = await condition(
          (_, currentState) => hasSettled(currentState) || !isTracked(),
          RUN_SETTLE_TIMEOUT_MS
        );
        if (!settled || !isTracked()) return;
      }

      /**
       * The report may still be in flight, so give it a fixed grace; a report
       * landing ends this wait, since listener 2 untracks the run and toasts.
       *
       * Deliberately NOT gated on `log[activityId].streamEnded`. That flag is
       * per ACTIVITY, not per worker, and opening this run's log in the modal or
       * the activity list spawns a second worker over the same entry. Reading it
       * would let whichever worker finishes first declare the log drained while
       * the other is still polling, untracking a run that went on to report —
       * losing its toast and re-offering a paid run on a diagnosed failure.
       */
      if (!hasReport()) {
        await condition(() => hasReport() || !isTracked(), REPORT_GRACE_MS);
      }

      /**
       * The run is over and produced no report: it was cancelled, or the agent
       * died before printing one. Untrack it, or the failure it diagnoses keeps
       * showing "Troubleshooting..." for the rest of the session and the user
       * cannot start another run after cancelling one.
       *
       * No toast. A cancel is the user's own doing, and an agent that died
       * without a report is explained in place by `AgentReport`.
       */
      if (!hasReport() && isTracked()) {
        dispatch(runEndedWithoutReport(activityId));
      }
    }
  });

  // 2. Completion toast, off the log stream.
  startListening({
    actionCreator: streamFinished,
    effect: (action, { dispatch, getState, extra }) => {
      const { activityId } = action.payload;

      // Every log batch of every activity lands here, so bail on the cheap
      // lookup before parsing anything.
      const run = selectTroubleshootingRun(getState(), activityId);
      if (!run) return;

      // The report is the agent's last stdout line, so it can land before the
      // stream closes. Parse each batch and fire on the first one that carries
      // it; untracking makes this at-most-once per run. A batch without one says
      // nothing — whether the run is finished is listener 1's question.
      if (!selectAgentReportByActivityId(getState(), activityId)) return;

      dispatch(runReported(activityId));
      notifyTroubleshootingCompleted({
        run,
        dispatch,
        intl: extra.getIntl()
      });
    }
  });
};

// Register against the production middleware singleton.
registerTroubleshootingAgentListeners(startAppListening);
